import { BehaviorSubject } from 'rxjs'
import { FREDERICK_API_ROOT } from '../constants'
import axios from 'axios'
import * as Sentry from '@sentry/react'

/** @returns {object|null} Parsed session user, or null if missing or invalid JSON */
export const parseStoredCurrentUser = () => {
    try {
        const raw = localStorage.getItem('currentUser')
        if (raw == null) return null
        return JSON.parse(raw)
    } catch {
        return null
    }
}

const currentUserSubject = new BehaviorSubject(parseStoredCurrentUser())

const clearLocalSession = () => {
    localStorage.removeItem('currentUser')
    Sentry.setUser(null)
    currentUserSubject.next(null)
}

const isNonFatalAxiosError = error => {
    if (!error?.isAxiosError) return false
    if (!error.response) {
        return error.message === 'Network Error'
    }
    return error.response.status === 401
}

/** Report axios failures to Sentry unless they are expected offline/auth noise. */
export const captureUnexpectedAxiosError = error => {
    if (!isNonFatalAxiosError(error)) {
        Sentry.captureException(error)
    }
}

const logout = () => {
    clearLocalSession()
    return AxiosWithAuth.delete('/logout').catch(captureUnexpectedAxiosError)
}

export const signupWithToken = token => {
    // this is a sign up flow so log out the current user
    return AxiosWithAuth.get(`/users/confirmation?confirmation_token=${token}`)
}

export const login = (emp_cd, password) => {
    return AxiosWithAuth.post(
        `/login`,
        { user: { emp_cd, password } },
        { headers: { 'Content-Type': 'application/json' } }
    ).then(res => {
        if (res.status === 200) {
            const data = res.data
            localStorage.setItem('currentUser', JSON.stringify(data))
            localStorage.setItem('selected_emp', data.selected_emp)
            currentUserSubject.next(data)
            Sentry.setUser({
                email: data.email,
                emp_cd: data.employee_code,
                is_admin: data.is_admin,
                selected_emp: data.selected_emp
            })
        }
        return res
    })
}

export const SELECTED_STORE_CHANGED_EVENT = 'selectedStoreChanged'
export const STORE_EMPLOYMENTS_CHANGED_EVENT = 'storeEmploymentsChanged'
export const STORE_EMPLOYMENTS_CHANGED_KEY = 'storeEmploymentsChangedAt'

/** Notify shift views to refetch store employments (e.g. after user deactivation). */
export const notifyStoreEmploymentsChanged = () => {
    sessionStorage.setItem(STORE_EMPLOYMENTS_CHANGED_KEY, String(Date.now()))
    window.dispatchEvent(new CustomEvent(STORE_EMPLOYMENTS_CHANGED_EVENT))
}

export const changeSelectedStore = emp_id => {
    localStorage.setItem('selected_emp', emp_id)
    localStorage.setItem('submit_for', emp_id)
    AxiosWithAuth.put('/users/me', { selected_emp: emp_id }).catch(captureUnexpectedAxiosError)
    window.dispatchEvent(new CustomEvent(SELECTED_STORE_CHANGED_EVENT, { detail: { employmentId: emp_id } }))
}

export const changeSelectedEmployee = emp_id => {
    localStorage.setItem('submit_for', emp_id)
}

const CSRF_HEADER = 'X-CSRF-Token'
const CSRF_METHODS = new Set(['post', 'put', 'patch', 'delete'])
// Hits ApplicationController (unlike GET `/`, which is a bare Rack proc).
const CSRF_BOOTSTRAP_PATH = '/users/me'

let csrfToken = null
let csrfBootstrapPromise = null

export const readCsrfTokenFromHeaders = headers => {
    if (!headers) return null
    return headers[CSRF_HEADER] || headers[CSRF_HEADER.toLowerCase()] || null
}

export const storeCsrfTokenFromHeaders = headers => {
    const token = readCsrfTokenFromHeaders(headers)
    if (token) csrfToken = token
    return token
}

/** Exposed for tests — clears in-memory CSRF state. */
export const resetCsrfTokenForTests = () => {
    csrfToken = null
    csrfBootstrapPromise = null
}

export const getCsrfToken = () => csrfToken

/**
 * Ensure a CSRF token is available before mutating requests.
 * Safe to call after reload: bootstraps via GET /users/me (401 is fine if the
 * error response still carries X-CSRF-Token).
 */
export const ensureCsrfToken = client => {
    if (csrfToken) return Promise.resolve(csrfToken)
    if (!csrfBootstrapPromise) {
        csrfBootstrapPromise = client
            .get(CSRF_BOOTSTRAP_PATH)
            .then(() => {
                if (!csrfToken) {
                    throw new Error('CSRF bootstrap did not return X-CSRF-Token')
                }
                return csrfToken
            })
            .catch(error => {
                // Authenticated or not, ApplicationController after_action should
                // have stamped X-CSRF-Token; the response interceptor stores it.
                if (csrfToken) return csrfToken
                throw error
            })
            .finally(() => {
                csrfBootstrapPromise = null
            })
    }
    return csrfBootstrapPromise
}

const configuredAxios = () => {
    const configured = axios.create({
        baseURL: FREDERICK_API_ROOT,
        withCredentials: true,
        headers: {
            Accept: 'application/json'
        }
    })

    configured.interceptors.request.use(async config => {
        const method = (config.method || '').toLowerCase()
        if (CSRF_METHODS.has(method)) {
            if (!csrfToken) {
                try {
                    await ensureCsrfToken(configured)
                } catch (_) {
                    // Do not block CSRF-exempt routes (login, password reset).
                    // Protected routes will fail authenticity checks server-side.
                }
            }
            if (csrfToken) {
                config.headers[CSRF_HEADER] = csrfToken
            }
        }
        return config
    })

    configured.interceptors.response.use(
        res => {
            storeCsrfTokenFromHeaders(res.headers)
            return res
        },
        error => {
            storeCsrfTokenFromHeaders(error.response?.headers)
            // Any status codes that falls outside the range of 2xx cause this function to trigger
            // Do something with response error
            const status = error.response?.status
            if (status == null) {
                return Promise.reject(error)
            }
            if (window.location.pathname.includes('/employment')) {
                if (status === 422) {
                    // token has already been validated
                    clearLocalSession()
                    window.location.pathname = '/login'
                }
            } else if (
                status === 401 &&
                !window.location.pathname.includes('/confirmation') &&
                window.location.pathname !== '/login' &&
                !window.location.pathname.includes('/forgotPassword') &&
                !window.location.pathname.includes('/resetPassword')
            ) {
                // Devise unauthenticated responses are [{ message: ... }].
                // Authorization denials also use 401 with {} / { error: ... } — do
                // not treat those as an expired session (avoids forced logout).
                const data = error.response?.data
                const looksLikeExpiredSession = Array.isArray(data)
                if (looksLikeExpiredSession) {
                    clearLocalSession()
                    window.location.pathname = '/login'
                }
            }
            return Promise.reject(error)
        }
    )
    return configured
}

export const AxiosWithAuth = configuredAxios()

export const authenticationService = {
    login,
    logout,
    AxiosWithAuth,
    changeSelectedStore,
    currentUser: currentUserSubject.asObservable(),
    get currentUserValue() {
        return currentUserSubject.value
    }
}

export function authHeader() {
    const currentUser = authenticationService.currentUserValue
    if (currentUser && currentUser.token) {
        return { Authorization: currentUser.token }
    } else {
        return {}
    }
}
